import { useState } from "react";
import { useToast } from "@chakra-ui/react";

/**
 * Custom hook for managing booking form state and submission
 * Handles form data, product/address selection, validation, and API submission
 */
export default function useBookingForm({ onSuccess }) {
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    propertyId: "",
    awsPlaceId: "",
    address: "",
    latitude: null,
    longitude: null,
    productId: "",
    durationHours: 1,
    durationMinutes: 0,
    bufferMinutes: 15,
    afterDate: "",
  });

  // Product dropdown state
  const [productSearch, setProductSearch] = useState("");
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [highlightedProductIndex, setHighlightedProductIndex] = useState(-1);

  // Selected address state
  const [selectedAddress, setSelectedAddress] = useState(null);

  // Handle client address selection
  const handleClientAddressSelect = (address, closeDropdownFn) => {
    setFormData({
      ...formData,
      propertyId: address.id,
      awsPlaceId: "",
      address: address.full_address,
      latitude: address.latitude,
      longitude: address.longitude,
    });
    setSelectedAddress(address);
    if (closeDropdownFn) closeDropdownFn();
  };

  // Handle AWS address selection
  const handleAwsAddressSelect = (address, closeDropdownFn) => {
    setFormData({
      ...formData,
      propertyId: "",
      awsPlaceId: address.place_id,
      address: address.formatted_address,
      latitude: null,
      longitude: null,
    });
    setSelectedAddress(address);
    if (closeDropdownFn) closeDropdownFn();
  };

  // Handle product selection
  const handleProductSelect = (product) => {
    setFormData({
      ...formData,
      productId: product.id
    });
    setSelectedProduct(product);
    setShowProductDropdown(false);
    setProductSearch("");

    // Auto-update duration if product has duration_minutes
    if (product.duration_minutes && product.duration_minutes > 0) {
      const hours = Math.floor(product.duration_minutes / 60);
      const minutes = product.duration_minutes % 60;
      setFormData(prev => ({
        ...prev,
        productId: product.id,
        durationHours: hours,
        durationMinutes: minutes,
      }));
    }
  };

  // Adjust duration by adding/subtracting minutes
  const adjustDuration = (minutesToAdd) => {
    const currentTotalMinutes = formData.durationHours * 60 + formData.durationMinutes;
    const newTotalMinutes = Math.max(0, currentTotalMinutes + minutesToAdd);
    const newHours = Math.floor(newTotalMinutes / 60);
    const newMinutes = newTotalMinutes % 60;

    setFormData({
      ...formData,
      durationHours: newHours,
      durationMinutes: newMinutes,
    });
  };

  // Update form field
  const updateFormField = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  // Handle form submission
  const handleSubmit = async (e, resetRecommendationsFn) => {
    e.preventDefault();
    if (!isFormSubmittable()) return;
    setSubmitting(true);

    // Clear previous route data if reset function provided
    if (resetRecommendationsFn) {
      resetRecommendationsFn();
    }

    try {
      const totalMinutes = formData.durationHours * 60 + formData.durationMinutes;
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

      const payload = {
        propertyId: formData.propertyId || null,
        awsPlaceId: formData.awsPlaceId || null,
        address: formData.address,
        latitude: formData.latitude,
        longitude: formData.longitude,
        productId: formData.productId || null,
        durationMinutes: totalMinutes,
        bufferMinutes: formData.bufferMinutes,
        afterDate: formData.afterDate || null,
      };

      const response = await fetch("/api/last_minute_bookings", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.success) {
        // Call success callback with booking request and recommendations
        if (onSuccess) {
          onSuccess(data);
        }

        toast({
          title: "Recommendations generated!",
          description: `Found ${data.recommendations.length} available time slot${data.recommendations.length !== 1 ? 's' : ''}`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error",
          description: data.error || data.errors?.join(", ") || "Failed to generate recommendations",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error("Failed to create booking:", err);
      toast({
        title: "Error",
        description: "Failed to create booking request",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Reset form to initial state
  const resetForm = () => {
    setFormData({
      propertyId: "",
      awsPlaceId: "",
      address: "",
      latitude: null,
      longitude: null,
      productId: "",
      durationHours: 1,
      durationMinutes: 0,
      bufferMinutes: 15,
      afterDate: "",
    });
    setSelectedProduct(null);
    setSelectedAddress(null);
    setProductSearch("");
    setShowProductDropdown(false);
  };

  // Populate form with data (for viewing previous requests)
  const populateForm = (data) => {
    const hours = Math.floor(data.duration_minutes / 60);
    const minutes = data.duration_minutes % 60;

    setFormData({
      propertyId: data.product?.id || "",
      awsPlaceId: "",
      address: data.address,
      latitude: null,
      longitude: null,
      productId: data.product?.id || "",
      durationHours: hours,
      durationMinutes: minutes,
      bufferMinutes: data.buffer_minutes,
    });

    if (data.product) {
      setSelectedProduct(data.product);
    }
  };

  // Get computed values
  const getTotalDurationMinutes = () => {
    return formData.durationHours * 60 + formData.durationMinutes;
  };

  const isFormSubmittable = () => {
    const address = (formData.address || "").trim();
    const durationHours = Number(formData.durationHours);
    const durationMinutes = Number(formData.durationMinutes);
    const bufferMinutes = Number(formData.bufferMinutes);
    const totalDurationMinutes = durationHours * 60 + durationMinutes;

    return (
      address.length > 0 &&
      Number.isFinite(totalDurationMinutes) &&
      totalDurationMinutes > 0 &&
      Number.isFinite(bufferMinutes) &&
      bufferMinutes >= 0
    );
  };

  return {
    // Form state
    formData,
    setFormData,
    updateFormField,
    submitting,

    // Product dropdown
    productSearch,
    setProductSearch,
    showProductDropdown,
    setShowProductDropdown,
    selectedProduct,
    setSelectedProduct,
    highlightedProductIndex,
    setHighlightedProductIndex,

    // Address
    selectedAddress,
    setSelectedAddress,

    // Handlers
    handleClientAddressSelect,
    handleAwsAddressSelect,
    handleProductSelect,
    adjustDuration,
    handleSubmit,

    // Helpers
    resetForm,
    populateForm,
    getTotalDurationMinutes,
    isFormSubmittable,
  };
}
